import {
  action,
  atom,
  onConnect,
  onUpdate,
  reatomAsync,
  withAbort,
} from "@reatom/framework";
import {
  ProductControllerSearchSortByEnum,
  ProductResponseV2Dto,
  ProductSourceDtoSourceEnum,
} from "definitions/product-storage";
import { API } from "src/api";
import { withDebounce, withIsLoadingAtom } from "src/common/pipes";
import { productSelectionStore } from "src/store/atoms";
import { isCanceledRequestError } from "src/util/isCanceledRequestError";

import { PriceRange } from "../../components/ProductsTable/components";
import { filterAtom, onFiltersChange, sortAtom } from "../atoms";
import {
  cursorHistoryAtom,
  currentPerPageAtom,
  resetCursorHistory,
  cursorHistoryLengthAtom,
  nextCursorAtom,
  addCursorToHistory,
} from "../atoms/cursorPagination";

const MAX_PRODUCTS_COUNT = 400;

export interface ProductTableData {
  id: number;
  logoUrl: string;
  name: string;
  article: string;
  priceRange: PriceRange;
  sources: ProductSourceDtoSourceEnum[];
  releaseDate: string | null;
}

export const productsAtom = atom<ProductTableData[]>([]);

export const searchProductFetcher = reatomAsync(
  async (ctx, cursor: string | undefined) => {
    const filterData = ctx.get(filterAtom);
    const sortData = ctx.get(sortAtom);

    const selectedProductSample = ctx.get(
      productSelectionStore.currentSelectionAtom,
    );

    try {
      const response = await API.productStorageApi
        .productControllerSearchV2(
          {
            query: filterData.search || undefined,
            categories: filterData.categoryIds?.map(Number),
            brands: filterData.brandIds?.map(Number),
            priceMin: filterData.priceFrom,
            priceMax: filterData.priceTo,
            currency: filterData.currency ?? "RUB",
            cursor,
            genders: filterData.genders,
            sources: filterData.sources,
            includeCustom: filterData.includeCustom,
            limit: ctx.get(currentPerPageAtom),
            sortBy:
              sortData?.type ?? ProductControllerSearchSortByEnum.Popularity,
            ...(filterData.viewAllSelected && {
              selectionId: selectedProductSample?.id,
            }),
            excludeSelectionIds: filterData.excludeSamples?.map(Number),
          },
          {
            signal: ctx.controller.signal,
          },
        )
        .then((res) => res.data);

      return response;
    } catch (error) {
      if (!isCanceledRequestError(error)) {
        console.error("Error fetching search products:", error);
      }

      throw error;
    }
  },
  "fetchSearchProductFetcher",
).pipe(withAbort(), withIsLoadingAtom());

const hasNext = atom(false);

const loadPage = action(async (ctx, cursor: string | undefined) => {
  const searchResult = await ctx.schedule(() =>
    searchProductFetcher(ctx, cursor),
  );
  const meta = searchResult?.meta;

  hasNext(ctx, meta?.hasNext ?? false);
  nextCursorAtom(ctx, meta?.cursor);

  return searchResult;
}, "loadPage");

const isRefreshing = atom(false, "isRefreshing");

const isLoaded = atom(false, "isLoaded");

const toReleaseDate = (value: unknown): string | null =>
  typeof value === "string" && value ? value : null;

const formatProductList = (
  product: ProductResponseV2Dto,
): ProductTableData => ({
  id: product.id || 0,
  logoUrl: product.logoUrl || "",
  name: product.title || "",
  article: product.article || "",
  priceRange: {
    minPrice: product?.minPrice,
    maxPrice: product?.maxPrice,
    currency: product?.currency || "RUB",
  },
  sources: product.sources || [],
  releaseDate: toReleaseDate(product.releaseDate),
});

export const addProducts = action(async (ctx) => {
  const nextCursor = ctx.get(nextCursorAtom);

  if (!nextCursor) {
    return;
  }

  if (nextCursor) {
    addCursorToHistory(ctx, nextCursor);
  }

  const currentProducts = ctx.get(productsAtom);
  const newProducts = await ctx.schedule(() => loadPage(ctx, nextCursor));
  const productList = newProducts?.data;

  if (!productList) {
    return;
  }

  const newProductsMapped = productList
    .map(formatProductList)
    .filter((product) => product.id);

  let updatedProducts = [...currentProducts, ...newProductsMapped];

  if (updatedProducts.length >= MAX_PRODUCTS_COUNT) {
    const countElementsToRemove = productList.length;
    updatedProducts = updatedProducts.slice(countElementsToRemove);
  }

  productsAtom(ctx, updatedProducts);
}, "addProducts");

export const updateProducts = action(async (ctx) => {
  isRefreshing(ctx, true);
  isLoaded(ctx, false);
  productsAtom(ctx, []);
  resetCursorHistory(ctx);
  nextCursorAtom(ctx, undefined);

  try {
    const newProducts = await ctx.schedule(() => loadPage(ctx, undefined));
    const productList = newProducts?.data;

    isLoaded(ctx, true);

    if (!productList?.length) {
      return;
    }

    const newProductsMapped = productList
      .map(formatProductList)
      .filter((product) => product.id);

    productsAtom(ctx, newProductsMapped);
  } finally {
    isRefreshing(ctx, false);
  }
}, "updateProducts");

const debouncedUpdateProducts = updateProducts.pipe(withDebounce(400));

onFiltersChange((ctx) => {
  debouncedUpdateProducts(ctx).catch(() => null);
});

onUpdate(sortAtom, (ctx) => {
  debouncedUpdateProducts(ctx).catch(() => null);
});

onConnect(productsAtom, (ctx) => {
  if (!ctx.get(isLoaded)) {
    updateProducts(ctx).catch(() => null);
  }
});

const isLoading = atom((ctx) => {
  const isFetch = ctx.spy(searchProductFetcher.isLoadingAtom);
  const products = ctx.spy(productsAtom);

  return isFetch && !products.length;
}, "isLoading");

export const loadNextPage = action(async (ctx) => {
  const nextCursor = ctx.get(nextCursorAtom);

  if (!nextCursor) {
    return;
  }

  addCursorToHistory(ctx, nextCursor);

  const newProducts = await ctx.schedule(() => loadPage(ctx, nextCursor));
  const productList = newProducts?.data;

  if (!productList?.length) {
    return;
  }

  const newProductsMapped = productList
    .map(formatProductList)
    .filter((product) => product.id);

  productsAtom(ctx, newProductsMapped);
}, "loadNextPage");

export const loadPreviousPage = action(async (ctx) => {
  const historyLength = ctx.get(cursorHistoryLengthAtom);

  if (historyLength <= 0) {
    return;
  }

  const updatedHistory = cursorHistoryAtom(ctx, (prev) => prev.slice(0, -1));
  const lastCursor = updatedHistory[updatedHistory.length - 1];

  const newProducts = await ctx.schedule(() => loadPage(ctx, lastCursor));
  const productList = newProducts?.data;

  if (!productList?.length) {
    return;
  }

  const newProductsMapped = productList
    .map(formatProductList)
    .filter((product) => product.id);

  productsAtom(ctx, newProductsMapped);
}, "loadPreviousPage");

export const resetToFirstPage = action(async (ctx) => {
  resetCursorHistory(ctx);
  await updateProducts(ctx);
}, "resetToFirstPage");

export const searchProductStore = {
  productsAtom,
  addProducts,
  updateProducts,
  isLoaded,
  loadNextPage,
  loadPreviousPage,
  resetToFirstPage,
  isFetchLoading: searchProductFetcher.isLoadingAtom,
  isLoading,
  isPaginationLoading: searchProductFetcher.isLoadingAtom,
  hasNext,
  isRefreshing,
};
